import { Box, Grid } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useQuery } from 'react-query'
import { invalidationActivityLogCache } from 'src/api/activity-log'
import { invalidateLocalityCache } from 'src/api/localities'
import { useGetServiceStations } from 'src/api/service-station'
import { GenericDialogLayout } from 'src/components/GenericDialogLayout'
import { MainButton } from 'src/components/MainButton'
import { useDialogCtx } from 'src/hooks/context-hooks'
import { z } from 'zod'
import { invalidateCustomer, useGetCustomerUsers } from '../../../api/customer'
import { invalidateDeviation, useAddDeviationFollowUp, useAddDeviationFollowUpAdmin, useGetDeviationDocuments } from '../../../api/deviation'
import BasicDatePicker from '../../../components/DateTimePicker'
import { AppForm, FormCheckbox, FormSelectField, FormTextField } from '../../../components/FormCore'
import { IUser } from '../../../generated-types'
import { DeviationDocumentType } from '../../../generated-types/deviation-document'
import { IDeviationPriority } from '../../../generated-types/deviation-priority'
import { EnumToSelectTypes } from '../../../generated-types/select-type'
import { useZodForm } from '../../../hooks/zod-form'
import { getUsers } from '../../../http'
import { Queries } from '../../../queries'
import { ShowDialogProps } from '../../../show-dialog'
import { EdgeCase, UserDetail, getUserDetail } from '../../../utils/getUserDetails'
import { invalidateMainComponentCache } from 'src/api/main-component'

interface CreateDeviationFollowUpDialogProps extends ShowDialogProps {
  customerId: EntityId
  deviationId: EntityId
  dueDate?: Date
  priority?: IDeviationPriority
  localityId: EntityId
  positionId?: EntityId
  validationRequired?: boolean
  defaultStatus?: boolean
}

interface CreateDeviationFollowUpDialogAdminProps extends ShowDialogProps {
  deviationId: EntityId
  dueDate?: Date
  priority?: IDeviationPriority
  defaultStatus?: boolean
}

interface CreateDeviationFollowUpFormProps extends ShowDialogProps {
  deviationId: EntityId
  customerUsers: IUser[]
  dueDate?: Date
  priority?: IDeviationPriority
  isLoading: boolean
  onSubmit: (form: any) => void
  serviceStations?: Array<{ id: number; name: string }>
  validationRequired?: boolean
  defaultStatus?: boolean
}

export const CreateDeviationFollowUpForm: React.FC<CreateDeviationFollowUpFormProps> = ({
  pop,
  customerUsers,
  deviationId,
  dueDate,
  priority,
  onSubmit,
  serviceStations = [],
  validationRequired = false,
  defaultStatus = false,
}) => {
  const { t } = useTranslation(['deviation', 'common'])

  const deviationPriorities = EnumToSelectTypes(IDeviationPriority, 'deviation')

  const deviationSchema = z
    .object({
      deviationId: z.string().or(z.number()).optional(),
      priority: z
        .object({
          id: z.number(),
        })
        .transform(value => value.id),
      responsiblePersonId: z
        .object(
          {
            id: z.string().nonempty(t('selectOneOfTheGivenOptions', { ns: 'common' })),
          },
          {
            required_error: t('selectOneOfTheGivenOptions', { ns: 'common' }),
            invalid_type_error: t('selectOneOfTheGivenOptions', { ns: 'common' }),
          },
        )
        .transform(value => value.id),
      dueDate: z
        .date()
        .or(z.string())
        .default(dueDate as Date),
      performedBy: z.string().nonempty(t('provideValidInput', { ns: 'common' })),
      description: z.string().nonempty(),
      status: z.boolean().optional(),
      fileId: z.any().optional(),
      serviceStationId: z
        .any()
        .optional()
        .transform(val =>
          val == null || val === ''
            ? undefined
            : typeof val === 'object' && val?.id != null
              ? Number(val.id)
              : Number(val),
        ),
    })
    .refine(
      data => !data.status || !validationRequired || (data.serviceStationId != null && data.serviceStationId > 0),
      {
        message: t('selectServiceStationForValidation', { ns: 'deviation' }),
        path: ['serviceStationId'],
      },
    )

  const form = useZodForm(deviationSchema, {
    defaultValues: {
      dueDate: dueDate as Date,
      priority: deviationPriorities[priority as IDeviationPriority],
      status: defaultStatus,
    },
  })
  return (
    <GenericDialogLayout
      title={t('addNewDeviationFollowUp', { ns: 'deviation' })}
      pop={pop}
      actions={[
        <MainButton
          key="submit"
          data-cy="submitAddFollowUp"
          onClick={() => {
            form.handleSubmit(onSubmit)()
          }}
        >
          {t('add', { ns: 'common' })}
        </MainButton>,
      ]}
      removedCloseButton={false}
    >
      <AppForm form={form} onSubmit={onSubmit}>
        <FormTextField type="hidden" name="deviationId" value={deviationId} sx={{ display: 'none' }} />
        <FormSelectField
          name="responsiblePersonId"
          data-cy="responsiblePerson"
          data={customerUsers!}
          label={t('responsiblePerson', { ns: 'deviation' })}
          getOptionLabel={option => getUserDetail(option, UserDetail.Any, EdgeCase.ReturnEmailForNewUser)}
          renderOption={(props: any, option) => (
            <Box {...props} key={option}>
              {getUserDetail(option, UserDetail.Any, EdgeCase.ReturnEmailForNewUser)}
            </Box>
          )}
          isOptionEqualToValue={(o, v) => o.id === v.id}
        />
        <Grid container>
          <Grid item xs={12} sm={4}>
            <FormTextField name="performedBy" label={t('performedBy', { ns: 'common' })} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <BasicDatePicker label={t('dueDate', { ns: 'common' })} name="dueDate" />
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormSelectField
              name="priority"
              data={deviationPriorities}
              label={t('priority', { ns: 'common' })}
              getOptionLabel={option => t(option.value.toLocaleLowerCase(), { ns: 'common' })}
              renderOption={(props: any, option) => (
                <Box {...props} key={option.id}>
                  {t(option.value.toLocaleLowerCase(), { ns: 'common' })}
                </Box>
              )}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              defaultValue={deviationPriorities[priority as IDeviationPriority]}
              data-cy="priority"
            />
          </Grid>
        </Grid>
        <FormTextField name="description" label={t('description', { ns: 'common' })} multiline rows={2} />
        <FormCheckbox name="status" label={t('closeDeviation', { ns: 'deviation' })} />
        {form.watch('status') && validationRequired && (
          <Grid item xs={12} sm={6} sx={{ mt: 1 }}>
            <FormSelectField
              name="serviceStationId"
              data={serviceStations}
              label={t('serviceStation', { ns: 'deviation' })}
              getOptionLabel={option => option?.name ?? ''}
              renderOption={(props: any, option) => (
                <Box {...props} key={option?.id}>
                  {option?.name}
                </Box>
              )}
              isOptionEqualToValue={(o, v) => o?.id === v?.id}
            />
          </Grid>
        )}
      </AppForm>
    </GenericDialogLayout>
  )
}

export const CreateDeviationFollowUpDialog: React.FC<CreateDeviationFollowUpDialogProps> = ({
  pop,
  customerId,
  deviationId,
  dueDate,
  priority,
  localityId,
  positionId,
  validationRequired = false,
  defaultStatus = false,
}) => {
  const { t } = useTranslation(['deviation', 'common'])
  const { data: customerUsers } = useGetCustomerUsers(customerId)
  const { data: serviceStations } = useGetServiceStations(customerId)
  const { data: deviationDocuments } = useGetDeviationDocuments(customerId, deviationId)
  const { mutateAsync, isLoading } = useAddDeviationFollowUp(customerId, deviationId)
  const { showWarningDialog } = useDialogCtx()
  const onSubmit = async form => {
    if (form.status) {
      const hasDraftDocuments = deviationDocuments?.some(
        doc => doc.type === DeviationDocumentType.SchemaReport && doc.formSchemaReport?.isDraft === true,
      )
      if (hasDraftDocuments) {
        showWarningDialog(
          t('draft-report-warning-title', { ns: 'deviation' }),
          t('draft-report-warning-description', { ns: 'deviation' }),
          false,
        )
        return
      }
    }
    await mutateAsync(form, {
      onSuccess: async () => {
        invalidationActivityLogCache.getCageActivityLogs()
        invalidateDeviation.invalidateAllDeviations(
          customerId as EntityId,
          localityId,
          undefined,
          deviationId,
          positionId,
        )
        invalidateDeviation.getAllDeviationsForScheduledWorkOrders(customerId as EntityId, localityId as EntityId)
        invalidateLocalityCache.getLocalityDeviationCounts(customerId as EntityId, localityId as EntityId)
        invalidateLocalityCache.getMaintenanceTypesCount(customerId as EntityId, localityId as EntityId)
        invalidateCustomer.getCustomersActivityCounts(customerId as EntityId)
        invalidateMainComponentCache.getMainComponents(customerId as EntityId, localityId as EntityId)
        pop()
      },
    })
  }
  return (
    <CreateDeviationFollowUpForm
      pop={pop}
      deviationId={deviationId}
      customerUsers={customerUsers as IUser[]}
      isLoading={isLoading}
      onSubmit={onSubmit}
      dueDate={dueDate}
      priority={priority}
      serviceStations={serviceStations ?? []}
      validationRequired={validationRequired}
      defaultStatus={defaultStatus}
    />
  )
}

export const CreateDeviationFollowUpDialogAdmin: React.FC<CreateDeviationFollowUpDialogAdminProps> = ({
  pop,
  deviationId,
  dueDate,
  priority,
  defaultStatus = false,
}) => {
  const { mutateAsync, isLoading } = useAddDeviationFollowUpAdmin(deviationId)
  const { data: customerUsers } = useQuery(Queries.Users, async () => {
    return await getUsers()
  })
  const onSubmit = async form => {
    await mutateAsync(form, {
      onSuccess: async () => {
        invalidateDeviation.invalidateAllDeviations()
        invalidationActivityLogCache.getCageActivityLogs()
        invalidateMainComponentCache.getMainComponents()
        pop()
      },
    })
  }
  return (
    <CreateDeviationFollowUpForm
      pop={pop}
      deviationId={deviationId}
      customerUsers={customerUsers as unknown as IUser[]}
      isLoading={isLoading}
      onSubmit={onSubmit}
      dueDate={dueDate}
      priority={priority}
      defaultStatus={defaultStatus}
    />
  )
}
